import type { ElementRef } from '@angular/core';
import { Injectable } from '@angular/core';
import type {
  BackendPushResponse,
  BackendUpdateResponse,
  DatastoreCollection,
} from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  TimeTrackingPunchesCollection,
  TimeTrackingSession,
} from '@freelancer/datastore/collections';
import {
  TIME_TRACKING_SESSION_ACTIONS,
  type Bid,
  type TimeTrackingSessionCollection,
} from '@freelancer/datastore/collections';
import {
  MediaDeviceResultStatus,
  MediaDevices,
} from '@freelancer/media-devices';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';
import { ModalService } from '@freelancer/ui';
import { ModalSize } from '@freelancer/ui/modal';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { SessionApi } from 'api-typings/timetracker/timetracker';
import type { Observable } from 'rxjs';
import { BehaviorSubject, filter, firstValueFrom, map } from 'rxjs';
import { DEFAULT_SCREENSHOT_OPTIONS } from './project-time-tracking.type';

@UntilDestroy({ className: 'ProjectTimeTracking' })
@Injectable({
  providedIn: 'root',
})
export class ProjectTimeTracking {
  stream?: MediaStream;
  timer: Timer;

  FIVE_MINS_IN_MS = 300_000;
  ONE_MIN_IN_MS = 60_000;

  videoElement: ElementRef<HTMLVideoElement>;
  canvasElement: ElementRef<HTMLCanvasElement>;

  private isSubmittingSubject$ = new BehaviorSubject<boolean>(false);
  isSubmitting$ = this.isSubmittingSubject$.asObservable();

  private currentlyTrackedBidSubject$ = new BehaviorSubject<Bid | undefined>(
    undefined,
  );
  currentlyTrackedBid$ = this.currentlyTrackedBidSubject$.asObservable();

  activeTrackingSessionCollection: DatastoreCollection<TimeTrackingSessionCollection>;
  activeSession$: Observable<TimeTrackingSession | undefined>;

  private handleInactiveStreamForBidId: (event: Event) => void;

  constructor(
    private mediaDevices: MediaDevices,
    private modalService: ModalService,
    private datastore: Datastore,
    private timeUtils: TimeUtils,
    private toastAlertService: ToastAlertService,
  ) {}

  setVideoAndCanvasElements(
    videoElement: ElementRef<HTMLVideoElement>,
    canvasElement: ElementRef<HTMLCanvasElement>,
  ): void {
    this.videoElement = videoElement;
    this.canvasElement = canvasElement;
  }

  async startTimeTracking(
    bid: Bid,
  ): Promise<BackendPushResponse<TimeTrackingSessionCollection> | undefined> {
    if (!this.videoElement) {
      throw new Error('Video element not set in project time tracking service');
    }

    if (!this.canvasElement) {
      throw new Error(
        'Canvas element not set in project time tracking service',
      );
    }

    if (this.stream?.active) {
      return;
    }
    this.setIsSubmitting(true);
    const createSessionResult = await this.startSession(bid);
    this.setIsSubmitting(false);
    return createSessionResult;
  }

  private async startSession(
    bid: Bid,
  ): Promise<BackendPushResponse<TimeTrackingSessionCollection> | undefined> {
    const displayMedia = await this.mediaDevices.getDisplayMedia({
      audio: false,
    });

    if (displayMedia.status !== MediaDeviceResultStatus.SUCCESS) {
      this.modalService.open('TimeTrackingPermissionDeniedModal', {
        inputs: { bid },
        showBackdrop: true,
        size: ModalSize.SMALL,
      });
      return;
    }

    this.stream = displayMedia.stream;
    if (!this.stream) {
      return;
    }

    this.handleInactiveStreamForBidId = () => {
      this.endTimeTracking(bid.id);
      this.toastAlertService.open({
        message: $localize`Stopping the screen recording has ended the current time tracking session.`,
        type: ToastAlertType.INFO,
        closeable: true,
        timeout: 7000,
      });
    };
    this.stream.addEventListener('inactive', this.handleInactiveStreamForBidId);

    this.videoElement.nativeElement.srcObject = this.stream;

    const canvasElement = this.canvasElement.nativeElement;
    const videoElement = this.videoElement.nativeElement;

    const {
      height = DEFAULT_SCREENSHOT_OPTIONS.HEIGHT,
      width = DEFAULT_SCREENSHOT_OPTIONS.WIDTH,
    } = this.mediaDevices.getMediaStreamSettings(this.stream, 'video');

    canvasElement.height = height;
    canvasElement.width = width;

    const createSessionResult =
      await this.datastore.createDocument<TimeTrackingSessionCollection>(
        'timeTrackingSession',
        {
          timeStarted: Date.now(),
          timeLastUpdated: Date.now(),
          duration: 0,
          manuallyTracked: false,
          userId: bid.bidderId,
          projectId: bid.projectId,
          bidId: bid.id,
          invoiced: false,
          trackingStoppedTime: undefined,
        },
        { action: TIME_TRACKING_SESSION_ACTIONS.START },
      );

    if (
      createSessionResult.status !== 'success' ||
      !this.isSessionApi(createSessionResult)
    ) {
      return createSessionResult;
    }

    this.activeTrackingSessionCollection =
      this.datastore.collection<TimeTrackingSessionCollection>(
        'timeTrackingSession',
        query =>
          query
            .where('bidId', '==', bid.id)
            .where('trackingStoppedTime', '==', undefined)
            .where('manuallyTracked', '==', false),
      );

    this.activeSession$ = this.activeTrackingSessionCollection
      .valueChanges()
      .pipe(map(sessions => sessions[0]));

    const sessionId = createSessionResult.id;

    // Id is apparently optional on the SessionApi type, but we should have it when creating new sessions
    // TODO: T288153 - Enforce sessionId in TimeTrackingSessionCollection transformer
    if (!sessionId) {
      return createSessionResult;
    }

    this.currentlyTrackedBidSubject$.next(bid);

    const screenshotCallback = (): void => {
      const screenshot = this.createScreenshot(
        height,
        width,
        canvasElement,
        videoElement,
      );

      if (screenshot) {
        this.createPunch(bid.projectId, bid.id, screenshot);

        // schedule next screenshot with a random delay between 5 and 6 minutes
        this.timer = this.timeUtils.setTimeout(
          screenshotCallback,
          this.getNextScreenshotDelay(),
        );
      }
    };

    screenshotCallback();
    return createSessionResult;
  }

  private getNextScreenshotDelay(): number {
    return (
      this.FIVE_MINS_IN_MS + Math.floor(Math.random() * this.ONE_MIN_IN_MS)
    );
  }

  private isSessionApi(
    response: TimeTrackingSessionCollection['Backend']['Push']['ReturnType'],
  ): response is SessionApi {
    return (response as SessionApi).id !== undefined;
  }

  private createScreenshot(
    height: number,
    width: number,
    canvas: HTMLCanvasElement,
    video: HTMLVideoElement,
  ): string | undefined {
    if (!this.stream || !this.stream?.active) {
      clearInterval(this.timer);
      return undefined;
    }

    // Obtain the 2D rendering context of the canvas element and draw the video image onto it
    canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
    // Remove the data URL prefix and encoding information from the canvas data URL
    return canvas
      .toDataURL('image/png')
      .replace(/^data:image\/?[A-z]*;base64,/, '');
  }

  private async createPunch(
    projectId: number,
    bidId: number,
    screenshot: string,
  ): Promise<void> {
    const sessionId = await firstValueFrom(
      this.activeSession$.pipe(
        map(session => session?.id),
        untilDestroyed(this),
      ),
    );

    if (!sessionId) {
      return;
    }

    this.datastore
      .collection<TimeTrackingPunchesCollection>('timeTrackingPunches')
      .push({
        projectId,
        sessionId,
        bidId,
        screenshot,
        timeCreated: Date.now(),
      });
  }

  async endTimeTracking(
    bidId: number,
  ): Promise<BackendUpdateResponse<TimeTrackingSessionCollection> | undefined> {
    this.setIsSubmitting(true);
    if (this.stream) {
      this.stream.removeEventListener(
        'inactive',
        this.handleInactiveStreamForBidId,
      );
    }

    this.activeTrackingSessionCollection =
      this.datastore.collection<TimeTrackingSessionCollection>(
        'timeTrackingSession',
        query =>
          query
            .where('bidId', '==', bidId)
            .where('trackingStoppedTime', '==', undefined)
            .where('manuallyTracked', '==', false),
      );

    this.activeSession$ = this.activeTrackingSessionCollection
      .valueChanges()
      .pipe(map(sessions => sessions[0]));

    const stopTrackingResult = await firstValueFrom(
      this.activeSession$.pipe(filter(isDefined), untilDestroyed(this)),
    ).then(session =>
      this.activeTrackingSessionCollection.update(session.id, {
        trackingStoppedTime: Date.now(),
      }),
    );

    if (this.stream) {
      this.mediaDevices.stopMediaStream(this.stream);
    }

    this.currentlyTrackedBidSubject$.next(undefined);
    this.setIsSubmitting(false);
    return stopTrackingResult;
  }

  private setIsSubmitting(isSubmitting: boolean): void {
    this.isSubmittingSubject$.next(isSubmitting);
  }

  /**
   * Stops the current stream and clears the tracked bid for when the current active session was stopped from a different app shell (eg. different tab).
   */
  stopStreaming(): void {
    if (!this.stream) {
      return;
    }

    this.stream.removeEventListener(
      'inactive',
      this.handleInactiveStreamForBidId,
    );
    this.mediaDevices.stopMediaStream(this.stream);
    this.currentlyTrackedBidSubject$.next(undefined);
  }
}
